import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Dashboard for displaying user activity and statistics
 */
const UserActivityDashboard = ({ userId, onClose }) => {
    const { getToken } = useAuth();
    const { showToast } = useToast();

    // State management
    const [loading, setLoading] = useState(true);
    const [activityData, setActivityData] = useState(null);
    const [error, setError] = useState(null);
    const [timeRange, setTimeRange] = useState('30d'); // 7d, 30d, 90d, 1y

    /**
     * Fetch user activity data
     */
    const fetchActivityData = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = await getToken();
            const response = await apiRequest(`/api/admin/users/${userId}/activity?range=${timeRange}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorMessage = await parseApiError(response);
                throw new ApiError(errorMessage, response.status, response);
            }

            const data = await response.json();
            setActivityData(data);

        } catch (error) {
            console.error('Error fetching activity data:', error);
            setError(error.message || 'Failed to load activity data');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Format date for display
     */
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Format duration in minutes to human readable
     */
    const formatDuration = (minutes) => {
        if (!minutes || minutes < 1) return '< 1 min';
        if (minutes < 60) return `${Math.round(minutes)} min`;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        return `${hours}h ${remainingMinutes}m`;
    };

    /**
     * Get activity type badge styling
     */
    const getActivityTypeBadge = (type) => {
        const baseClasses = 'px-2 py-1 rounded text-xs font-medium';

        switch (type?.toLowerCase()) {
            case 'call':
                return `${baseClasses} bg-green-900/30 text-green-400`;
            case 'login':
                return `${baseClasses} bg-blue-900/30 text-blue-400`;
            case 'parrot_created':
                return `${baseClasses} bg-purple-900/30 text-purple-400`;
            case 'parrot_updated':
                return `${baseClasses} bg-yellow-900/30 text-yellow-400`;
            case 'subscription_changed':
                return `${baseClasses} bg-teal-900/30 text-teal-400`;
            default:
                return `${baseClasses} bg-gray-900/30 text-gray-400`;
        }
    };

    // Load activity data when component mounts or time range changes
    useEffect(() => {
        if (userId) {
            fetchActivityData();
        }
    }, [userId, timeRange]);

    if (loading) {
        return (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
                    <div className="space-y-3">
                        <div className="h-4 bg-gray-700 rounded"></div>
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                <div className="text-center">
                    <div className="text-red-400 mb-2">⚠️</div>
                    <p className="text-red-300 mb-4">{error}</p>
                    <button
                        onClick={fetchActivityData}
                        className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors dark:text-white"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-800 rounded-lg border border-gray-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-medium text-white dark:text-white">User Activity & Statistics</h3>
                <div className="flex items-center space-x-4">
                    {/* Time Range Selector */}
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="px-3 py-1 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                    >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="1y">Last year</option>
                    </select>

                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white dark:text-white"
                    >
                        ✕
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
                {/* Statistics Overview */}
                <div>
                    <h4 className="text-md font-medium text-white mb-4 dark:text-white">Overview</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-gray-700/50 rounded-lg p-4">
                            <div className="text-2xl font-bold text-green-400">
                                {activityData?.stats?.total_calls || 0}
                            </div>
                            <div className="text-sm text-gray-400">Total Calls</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {activityData?.stats?.avg_calls_per_day || 0} avg/day
                            </div>
                        </div>

                        <div className="bg-gray-700/50 rounded-lg p-4">
                            <div className="text-2xl font-bold text-blue-400">
                                {formatDuration(activityData?.stats?.total_minutes || 0)}
                            </div>
                            <div className="text-sm text-gray-400">Call Duration</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {formatDuration(activityData?.stats?.avg_call_duration || 0)} avg
                            </div>
                        </div>

                        <div className="bg-gray-700/50 rounded-lg p-4">
                            <div className="text-2xl font-bold text-purple-400">
                                {activityData?.stats?.total_parrots || 0}
                            </div>
                            <div className="text-sm text-gray-400">Parrots Created</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {activityData?.stats?.active_parrots || 0} active
                            </div>
                        </div>

                        <div className="bg-gray-700/50 rounded-lg p-4">
                            <div className="text-2xl font-bold text-teal-400">
                                {activityData?.stats?.login_count || 0}
                            </div>
                            <div className="text-sm text-gray-400">Logins</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {formatDate(activityData?.stats?.last_login)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Call Activity Chart */}
                {activityData?.call_activity && activityData.call_activity.length > 0 && (
                    <div>
                        <h4 className="text-md font-medium text-white mb-4 dark:text-white">Call Activity</h4>
                        <div className="bg-gray-700/30 rounded-lg p-4">
                            <div className="flex items-end space-x-1 h-32">
                                {activityData.call_activity.map((day, index) => (
                                    <div key={index} className="flex-1 flex flex-col items-center">
                                        <div
                                            className="bg-teal-500 rounded-t w-full transition-all hover:bg-teal-400"
                                            style={{
                                                height: `${Math.max((day.calls / Math.max(...activityData.call_activity.map(d => d.calls))) * 100, 2)}%`
                                            }}
                                            title={`${day.date}: ${day.calls} calls`}
                                        ></div>
                                        <div className="text-xs text-gray-400 mt-1 transform -rotate-45 origin-left">
                                            {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Recent Activity */}
                <div>
                    <h4 className="text-md font-medium text-white mb-4 dark:text-white">Recent Activity</h4>
                    {activityData?.recent_activity && activityData.recent_activity.length > 0 ? (
                        <div className="space-y-3">
                            {activityData.recent_activity.slice(0, 10).map((activity, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <span className={getActivityTypeBadge(activity.type)}>
                                            {activity.type.replace('_', ' ')}
                                        </span>
                                        <div>
                                            <p className="text-sm text-white dark:text-white">{activity.description}</p>
                                            <p className="text-xs text-gray-400">{formatDate(activity.timestamp)}</p>
                                        </div>
                                    </div>
                                    {activity.metadata && (
                                        <div className="text-xs text-gray-400">
                                            {activity.metadata.duration && `${formatDuration(activity.metadata.duration)}`}
                                            {activity.metadata.cost && ` • $${(activity.metadata.cost / 100).toFixed(2)}`}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-4xl mb-4">📊</div>
                            <p className="text-gray-400">No recent activity found</p>
                        </div>
                    )}
                </div>

                {/* Top Parrots by Usage */}
                {activityData?.top_parrots && activityData.top_parrots.length > 0 && (
                    <div>
                        <h4 className="text-md font-medium text-white mb-4 dark:text-white">Most Active Parrots</h4>
                        <div className="space-y-2">
                            {activityData.top_parrots.slice(0, 5).map((parrot, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                                    <div>
                                        <p className="text-sm text-white font-medium dark:text-white">{parrot.name}</p>
                                        <p className="text-xs text-gray-400">Created {formatDate(parrot.created_at)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-teal-400 font-medium">{parrot.call_count} calls</p>
                                        <p className="text-xs text-gray-400">{formatDuration(parrot.total_minutes)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserActivityDashboard;

